import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ChevronDown, LogOut, Settings as SettingsIcon, FileText, Users, Tag, CreditCard, Building, MapPin, FileSpreadsheet, Mail, BarChart3, Clock, AlertTriangle } from 'lucide-react';
import Login from './components/Login';
import ResetPassword from './components/ResetPassword';
import StatementManagement from './components/StatementManagement';
import UserManagement from './components/UserManagement';
import VendorManagement from './components/VendorManagement';
import CreditCardManagement from './components/CreditCardManagement';
import MasterCardManagement from './components/MasterCardManagement';
import GLAccountManagement from './components/GLAccountManagement';
import LocationManagement from './components/LocationManagement';
import EmailConfiguration from './components/EmailConfiguration';
import AdminExpenseDashboard from './components/AdminExpenseDashboard';
import TimeAuditConfiguration from './components/TimeAuditConfiguration';
import TimeAuditUploads from './components/TimeAuditUploads';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('statements');
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [receiptIdFromUrl, setReceiptIdFromUrl] = useState(null);
  const settingsDropdownRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      // Set default authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Verify token by fetching user profile
      axios.get('/api/auth/profile')
        .then(response => {
          if (response.data.role === 'admin' || response.data.role === 'viewer') {
            setUser(response.data);
          } else {
            localStorage.removeItem('adminToken');
            delete axios.defaults.headers.common['Authorization'];
          }
        })
        .catch(() => {
          localStorage.removeItem('adminToken');
          delete axios.defaults.headers.common['Authorization'];
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Check for receipt deep link or reset password URL
  useEffect(() => {
    const checkForReceiptLink = () => {
      const path = window.location.pathname;
      console.log('Checking path for receipt link:', path);

      // Check if this is a reset password link
      if (path.startsWith('/reset-password/')) {
        return; // Don't process reset password URLs
      }

      const receiptMatch = path.match(/^\/receipt\/([a-f0-9-]+)$/i);
      if (receiptMatch && receiptMatch[1]) {
        console.log('Receipt ID found:', receiptMatch[1]);
        setReceiptIdFromUrl(receiptMatch[1]);
        setActiveTab('statements');
        // Update URL to clean path without reloading
        window.history.replaceState({}, '', '/');
      }
    };

    // Check immediately on mount
    checkForReceiptLink();

    // Listen for navigation events (browser back/forward buttons)
    const handlePopState = () => {
      console.log('popstate event fired');
      checkForReceiptLink();
    };

    window.addEventListener('popstate', handlePopState);

    // Check URL periodically to catch external link clicks (more frequent)
    const intervalId = setInterval(checkForReceiptLink, 100);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      clearInterval(intervalId);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (settingsDropdownRef.current && !settingsDropdownRef.current.contains(event.target)) {
        setShowSettingsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogin = (userData, token) => {
    setUser(userData);
    localStorage.setItem('adminToken', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('adminToken');
    delete axios.defaults.headers.common['Authorization'];
  };

  const handleSettingsItemClick = (tab) => {
    setActiveTab(tab);
    setShowSettingsDropdown(false);
  };

  const handleReceiptProcessed = () => {
    setReceiptIdFromUrl(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900">Loading...</h2>
        </div>
      </div>
    );
  }

  const settingsMenuItems = [
    { id: 'users', label: 'User Management', icon: Users },
    { id: 'vendors', label: 'Vendors', icon: Tag },
    { id: 'creditcards', label: 'Credit Cards', icon: CreditCard },
    { id: 'mastercards', label: 'Master Cards', icon: Building },
    { id: 'glaccounts', label: 'GL Accounts', icon: FileSpreadsheet },
    { id: 'locations', label: 'Locations', icon: MapPin },
    { id: 'configuration', label: 'Email Configuration', icon: Mail },
    { id: 'timeauditconfig', label: 'Time Audit Config', icon: Clock },
  ];

  const renderTabContent = () => {
    const isViewer = user?.role === 'viewer';

    switch (activeTab) {
      case 'dashboard':
        return <AdminExpenseDashboard />;
      case 'statements':
        return <StatementManagement receiptIdFromUrl={receiptIdFromUrl} onReceiptProcessed={handleReceiptProcessed} isViewer={isViewer} />;
      case 'users':
        return <UserManagement isViewer={isViewer} />;
      case 'vendors':
        return <VendorManagement isViewer={isViewer} />;
      case 'creditcards':
        return <CreditCardManagement isViewer={isViewer} />;
      case 'mastercards':
        return <MasterCardManagement isViewer={isViewer} />;
      case 'glaccounts':
        return <GLAccountManagement isViewer={isViewer} />;
      case 'locations':
        return <LocationManagement isViewer={isViewer} />;
      case 'configuration':
        return <EmailConfiguration isViewer={isViewer} />;
      case 'timeauditconfig':
        return <TimeAuditConfiguration isViewer={isViewer} />;
      case 'timeaudit':
        return <TimeAuditUploads isViewer={isViewer} />;
      default:
        return <StatementManagement receiptIdFromUrl={receiptIdFromUrl} onReceiptProcessed={handleReceiptProcessed} isViewer={isViewer} />;
    }
  };

  const isSettingsActive = ['users', 'vendors', 'creditcards', 'mastercards', 'glaccounts', 'locations', 'configuration', 'timeauditconfig'].includes(activeTab);

  // Check if we're on the reset password page
  if (window.location.pathname.startsWith('/reset-password/')) {
    return <ResetPassword />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {user ? (
        <div className="flex flex-col min-h-screen">
          {/* Top Navigation Bar */}
          <nav className="bg-white border-b border-gray-200 shadow-sm">
            <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between h-16">
                {/* Left side - Logo and Menu */}
                <div className="flex">
                  <div className="flex-shrink-0 flex items-center">
                    <h1 className="text-xl font-bold text-gray-900">Receipt Manager</h1>
                  </div>
                  <div className="hidden sm:ml-8 sm:flex sm:space-x-4 sm:items-center">
                    {/* Dashboard Tab */}
                    <button
                      onClick={() => setActiveTab('dashboard')}
                      className={`inline-flex items-center px-4 py-2 border-b-2 text-sm font-medium transition-colors duration-200 h-16 ${
                        activeTab === 'dashboard'
                          ? 'border-primary-600 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <BarChart3 className="w-4 h-4 mr-2" />
                      Dashboard
                    </button>

                    {/* Statements Tab */}
                    <button
                      onClick={() => setActiveTab('statements')}
                      className={`inline-flex items-center px-4 py-2 border-b-2 text-sm font-medium transition-colors duration-200 h-16 ${
                        activeTab === 'statements'
                          ? 'border-primary-600 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Statements
                    </button>

                    {/* Time Audit Tab */}
                    <button
                      onClick={() => setActiveTab('timeaudit')}
                      className={`inline-flex items-center px-4 py-2 border-b-2 text-sm font-medium transition-colors duration-200 h-16 ${
                        activeTab === 'timeaudit'
                          ? 'border-primary-600 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      Time Audit
                    </button>

                    {/* Settings Dropdown - Hidden for viewers */}
                    {user.role !== 'viewer' && (
                      <div className="relative h-16 flex items-center" ref={settingsDropdownRef}>
                        <button
                          onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
                          className={`inline-flex items-center px-4 py-2 border-b-2 text-sm font-medium transition-colors duration-200 h-full ${
                            isSettingsActive
                              ? 'border-primary-600 text-primary-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          <SettingsIcon className="w-4 h-4 mr-2" />
                          Settings
                          <ChevronDown className={`ml-1 w-4 h-4 transition-transform duration-200 ${showSettingsDropdown ? 'transform rotate-180' : ''}`} />
                        </button>

                        {/* Dropdown Menu */}
                        {showSettingsDropdown && (
                          <div className="absolute left-0 top-full w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                            <div className="py-1" role="menu">
                              {settingsMenuItems.map((item) => {
                                const Icon = item.icon;
                                return (
                                  <button
                                    key={item.id}
                                    onClick={() => handleSettingsItemClick(item.id)}
                                    className={`w-full text-left px-4 py-2 text-sm flex items-center transition-colors duration-150 ${
                                      activeTab === item.id
                                        ? 'bg-primary-50 text-primary-700'
                                        : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                                    role="menuitem"
                                  >
                                    <Icon className="w-4 h-4 mr-3" />
                                    {item.label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Right side - User info and Sign out */}
                <div className="flex items-center">
                  <div className="flex items-center space-x-3">
                    <div className="hidden sm:block text-right">
                      <p className="text-sm font-medium text-gray-700">
                        {user.firstName} {user.lastName}
                      </p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                    <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-sm font-medium text-primary-700">
                        {user.firstName?.[0]}{user.lastName?.[0]}
                      </span>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Menu */}
            <div className="sm:hidden border-t border-gray-200">
              <div className="px-2 pt-2 pb-3 space-y-1">
                <button
                  onClick={() => setActiveTab('statements')}
                  className={`block w-full text-left px-3 py-2 rounded-md text-base font-medium ${
                    activeTab === 'statements'
                      ? 'bg-primary-100 text-primary-900'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <FileText className="w-4 h-4 inline mr-2" />
                  Statements
                </button>
                <button
                  onClick={() => setActiveTab('timeaudit')}
                  className={`block w-full text-left px-3 py-2 rounded-md text-base font-medium ${
                    activeTab === 'timeaudit'
                      ? 'bg-primary-100 text-primary-900'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Clock className="w-4 h-4 inline mr-2" />
                  Time Audit
                </button>

                <div className="px-3 py-2 text-sm font-medium text-gray-500 uppercase tracking-wider">
                  Settings
                </div>
                {settingsMenuItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setActiveTab(item.id)}
                      className={`block w-full text-left px-3 py-2 rounded-md text-base font-medium ${
                        activeTab === item.id
                          ? 'bg-primary-100 text-primary-900'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="w-4 h-4 inline mr-2" />
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </nav>

          {/* Main Content */}
          <main className="flex-1">
            <div className="py-6">
              <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
                {renderTabContent()}
              </div>
            </div>
          </main>
        </div>
      ) : (
        <Login onLogin={handleLogin} />
      )}
    </div>
  );
}

export default App;
